/**
 * 描边（编辑→描边…，DESIGN.md §6.1 补充）。
 *
 * Photoshop 的描边 = 沿选区边缘画一条环形色带，位置分三种：内部（环在
 * 选区里那一侧）、居中（骑在边缘上）、外部（环在选区外那一侧）。
 * 环形状不用自己算——`engine/selection/ops.ts` 已经有 expand/contract/border
 * 这几个形态学原语（选区扩展/收缩菜单在用），描边只是拿它们做一次减法：
 * - 内部：mask − contract(mask, width)
 * - 外部：expand(mask, width) − mask
 * - 居中：border(mask, width)（已经是现成的减法）
 *
 * 没有选区时描的是整个画布的边缘——跟填充前景色"没选区就整层/整画布"
 * 同一个 Photoshop 行为，但不能直接把"铺满整个画布的矩形选区"丢给
 * expand/contract：这两个形态学原语只认**选区内部存的值发生跳变**的地方
 * 是边——一个铺满整个文档、四周再没有别的像素可比较的矩形，从它们的角度
 * 看跟到处都选中没有区别，压根没有跳变可找，会原样把矩形还回来（不是
 * bug，是"全选没有边缘"这条既有的性能捷径，选区扩展/收缩菜单在正常
 * 全选下用得着）。画布边缘这个"边"是文档尺寸本身带来的，不是选区数据
 * 里的跳变，只能自己按几何算：外圈矩形减去往里缩 `width` 的内圈矩形。
 */
import { border, contract, expand, fromRect, subtract } from '../engine/selection/ops';
import { applyFill } from '../engine/fill/apply';
import type { FillBlendOptions } from './selection-edit';
import type { AppStore, RgbColor } from './store';

export type StrokePosition = 'inside' | 'center' | 'outside';

export interface StrokeOptions extends FillBlendOptions {
  /** 描边宽度，文档像素，向下取整到 >= 1。 */
  readonly width: number;
  readonly position: StrokePosition;
}

/**
 * 沿选区（或没有选区时的整个画布）边缘描一条环形色带。
 * 一次调用 = 一条历史。返回是否有修改发生（环形区域落在文档外时可能为空）。
 */
export function strokeSelection(store: AppStore, color: RgbColor, options: StrokeOptions): boolean {
  const document = store.document;
  if (document === null) return false;
  const target = store.activeRasterLayerId();
  if (target === null) return false;
  const width = Math.max(1, Math.round(options.width));

  const rawSelection = store.selection;
  let ring;
  if (rawSelection !== null && !rawSelection.isEmpty) {
    ring =
      options.position === 'center'
        ? border(rawSelection, width)
        : options.position === 'inside'
          ? subtract(rawSelection, contract(rawSelection, width))
          : subtract(expand(rawSelection, width), rawSelection);
  } else {
    // 没有选区：画布边缘没有"外部"（画布外没有像素可画），三种位置统一按
    // 内部处理——环贴着画布边缘往里缩 width 像素，跟真实 Photoshop 在这个
    // 边界情况下的表现一致
    const { width: docW, height: docH } = document.size;
    const outer = fromRect(docW, docH, { x: 0, y: 0, width: docW, height: docH });
    const inset = Math.min(width, Math.floor(Math.min(docW, docH) / 2));
    const innerW = Math.max(0, docW - inset * 2);
    const innerH = Math.max(0, docH - inset * 2);
    const inner = fromRect(docW, docH, { x: inset, y: inset, width: innerW, height: innerH });
    ring = subtract(outer, inner);
  }

  const bounds = ring.contentBounds();
  if (bounds === null) return false;

  const transaction = store.beginPixelTransaction('描边');
  const touched = applyFill(
    store.tiles,
    target,
    bounds,
    () => [color.r, color.g, color.b, 255],
    transaction,
    {
      selection: ring,
      opacity: options.opacity ?? 1,
      ...(options.blendMode === undefined ? {} : { blendMode: options.blendMode }),
    },
  );
  if (touched.length === 0) return false;
  store.commitPixels('描边', transaction.end());
  store.invalidatePixels(touched);
  return true;
}
